<template>
  <div>
    <ValidationProvider :rules="{ required: required }" v-slot="{ errors, failed, passed }">
      <div
        :class="[failed && 'lib__field--error', passed && (zipCodeOrSubCity !== null && zipCodeOrSubCity !== '') && 'lib__field--valid']"
        class="lib__field">
        <label v-if="label" class="lib__field__label">{{ label }}</label>
        <input type="text" @blur="onBlur()" @click="onClick()" :placeholder="placeHolder" v-model="zipCodeOrSubCity"
               :class="inputClass" />
        <error-message :error="errors[0]" data-cy="textbox-error-message"></error-message>
        <ul v-if="subCities.length" :class="unorderedListContainerClass">
          <li v-for="(subCity, n) in subCities" :key="n" :class="suggestionItemClass" @click="subCitySelected(subCity)">
            <span :class="suggestionItemTextClass">
              {{ subCity.zipCode }} {{ subCity.cityName }}
            </span>
          </li>
        </ul>
      </div>
    </ValidationProvider>
  </div>

</template>

<script lang="ts">
import Vue from 'vue'
import { Prop, Watch, Component } from 'vue-property-decorator'
import { SubCity } from '@/models'

@Component({
  name: 'zipCode-locality-selector'
})
export default class ZipCodeLocalitySelector extends Vue {
  //#region [Property]
  @Prop({ type: Boolean, required: false, default: false }) public required!: boolean
  @Prop({ type: String, required: false, default: '' }) public inputClass!: string
  @Prop({ type: String, required: false, default: '' }) public placeHolder!: string
  @Prop({
    type: Function,
    required: false,
    default: () => {
      /* */
    }
  })
  public onClick!: () => void
  @Prop({
    type: Function,
    required: false,
    default: () => {
      /* */
    }
  })
  public onBlur!: () => void
  @Prop({ type: String, required: false, default: '' }) public unorderedListContainerClass!: string
  @Prop({ type: String, required: false, default: '' }) public suggestionItemClass!: string
  @Prop({ type: String, required: false, default: '' }) public suggestionItemTextClass!: string
  @Prop({ type: Object, required: false, default: null }) public value!: SubCity | null
  @Prop({ type: String, required: false, default: '' }) public label!: string
  //#endregion

  //#region [Data]
  public subCities: SubCity[] = []
  public zipCodeOrSubCity: string = ''
  //#endregion

  //#region [Watch]
  @Watch('zipCodeOrSubCity', { deep: false, immediate: false })
  public zipCodeWatcher() {
    this.zipCodeWatcherHandler()
  }

  @Watch('zipCodeOrSubCity')
  public onZipCodeOrSubictyChange() {
    if (this.zipCodeOrSubCity === '') {
      this.subCitySelected(null)
    }
  }

  @Watch('value')
  public onSubCity() {
    if (this.value) {
      this.fillInputValue(this.value)
    }
  }

  //endregion

  //#region [Method]
  public async mounted() {
    await this.zipCodeWatcherHandler()
    if (this.value) {
      this.fillInputValue(this.value)
    }
  }

  public async zipCodeWatcherHandler(): Promise<void> {
    if (this.zipCodeOrSubCity && this.zipCodeOrSubCity.trim() && this.zipCodeOrSubCity.trim().length) {
      this.zipCodeOrSubCity.trim().match(/^\d/)
        ? await this.autoCompleteByZipCode(this.zipCodeOrSubCity)
        : await this.autoCompleteByLocality(this.zipCodeOrSubCity)
    }
  }

  public async autoCompleteByZipCode(zipCode: string): Promise<void> {
    if (zipCode?.length !== 4) {
      return
    }

    const subCities = await this.getSubcities(zipCode)
    if (!!subCities.length) {
      this.subCities = subCities
    } else {
      this.setDefaultSubCity(zipCode, '')
    }
  }

  public async autoCompleteByLocality(locality: string): Promise<void> {
    if (!locality) {
      return
    }

    const subCities = await this.getSubcities()
    if (!!subCities.length) {
      const filteredSubcities: SubCity[] = subCities.filter((subCity: SubCity) =>
        subCity.cityName.startsWith(locality.toUpperCase())
      )
      if (filteredSubcities && filteredSubcities.length) {
        this.subCities =
          filteredSubcities.length > 8
            ? filteredSubcities.filter((subCity: SubCity, index: number) => index <= 8)
            : filteredSubcities
      } else {
        this.setDefaultSubCity('', locality)
      }
    }
  }

  public clearAutocomplete() {
    this.subCities = []
    this.zipCodeOrSubCity = ''
  }

  public fillInputValue(subCity: SubCity): void {
    if (subCity.zipCode === '') {
      this.zipCodeOrSubCity = `${subCity.cityName}`
    } else if (subCity.cityName === '') {
      this.zipCodeOrSubCity = `${subCity.zipCode}`
    } else {
      this.zipCodeOrSubCity = `${subCity.zipCode} - ${subCity.cityName}`
    }
  }

  public subCitySelected(subCity: SubCity | null) {
    this.$emit('input', subCity)
    if (subCity) {
      this.clearAutocomplete()
      this.fillInputValue(subCity)
    } else {
      this.subCities = []
    }
  }

  private async getSubcities(zipCode?: string): Promise<SubCity[]> {
    let url = 'subcities'
    if (zipCode !== undefined) {
      url += `?zipcode=${zipCode}`
    }

    const subCities = await this.$addressRepository
      .get<SubCity[]>(url, {
        cache: {
          cachePredicate: (status) => true,
        }
      })
      .then((response) => response.data)

    return subCities.filter((sc) => !sc.isFictive)
  }

  private setDefaultSubCity(zipCode: string, cityName: string): void {
    this.subCitySelected(null)
    this.subCities = [
      {
        georesSubcityId: -1,
        zipCode,
        cityName,
        municipality: {
          georesMunicipalityId: -1,
          name: ''
        },
        isFictive: false,
        connectMyHomeActivated: false,
        connectMyHomeActivationDate: null,
        connectMyHomeInformation: null,
        gridOperators: null,
        gridManagers: {
          electricityManager: null,
          gasManager: null
        },
        operationalOffices: {
          electricityOffice: null,
          gasOffice: null
        }
      }
    ]
  }

  //#endregion
}
</script>

<style scoped>
.zipcode-input {
    box-sizing: border-box;
    height: 50px;
    width: 320px;
    border: 1px solid #c1c7cc;
    border-radius: 25px;
    background-color: #ffffff;
    padding: 17px;
}

.autocomplete-suggestions {
    margin-left: -33px;
    padding-left: 30px;
    margin-top: 5px;
    min-width: 320px;
    z-index: 2;
}

.autocomplete-suggestion-item {
    list-style: none;
    padding-top: 20px;
    padding-left: 14px;
    height: 50px;
    background-color: #ffffff;
    box-shadow: 0 4px 8px 0 rgba(12, 0, 51, 0.1);
}

.autocomplete-suggestion-text {
    height: 22px;
    width: 320px;
    color: #3a3f44;
    font-family: 'Open Sans';
    font-size: 16px;
    letter-spacing: 0;
    line-height: 22px;
}
</style>
